import React, { useState, useEffect, useRef } from 'react';
import { FileText, Loader2, Palette, Languages, CheckCircle, ChevronDown, Globe, Image, Type } from 'lucide-react';
import { toast } from 'react-hot-toast';
import axios from '../../utils/axios';

interface TemplatePDFGeneratorProps {
  menuId: string;
  currentLanguage?: string;
}

const TemplatePDFGenerator: React.FC<TemplatePDFGeneratorProps> = ({ 
  menuId, 
  currentLanguage = 'it' 
}) => {
  const [templates] = useState([
    { id: 'elegant', name: 'Elegant', description: '' },
    { id: 'doppia', name: 'Doppia', description: '' },
    { id: 'mixology', name: 'Mixology', description: '' },
    { id: 'minimalist', name: 'Minimalist', description: '' },
    { id: 'vintage', name: 'Vintage', description: '' },
  ]);
  
  const languages = [
    { code: 'it', name: 'Italiano', native: 'Italiano' },
    { code: 'en', name: 'Inglese', native: 'English' },
    { code: 'fr', name: 'Francese', native: 'Français' },
    { code: 'es', name: 'Spagnolo', native: 'Español' },
    { code: 'de', name: 'Tedesco', native: 'Deutsch' },
    { code: 'pt', name: 'Portoghese', native: 'Português' },
    { code: 'ru', name: 'Russo', native: 'Русский' },
    { code: 'zh', name: 'Cinese', native: '中文' },
    { code: 'ja', name: 'Giapponese', native: '日本語' },
    { code: 'ar', name: 'Arabo', native: 'العربية' },
    { code: 'hi', name: 'Hindi', native: 'हिन्दी' },
    { code: 'ko', name: 'Coreano', native: '한국어' },
    { code: 'nl', name: 'Olandese', native: 'Nederlands' },
    { code: 'pl', name: 'Polacco', native: 'Polski' },
    { code: 'tr', name: 'Turco', native: 'Türkçe' },
    { code: 'sv', name: 'Svedese', native: 'Svenska' },
    { code: 'da', name: 'Danese', native: 'Dansk' },
    { code: 'fi', name: 'Finlandese', native: 'Suomi' },
    { code: 'no', name: 'Norvegese', native: 'Norsk' },
    { code: 'cs', name: 'Ceco', native: 'Čeština' },
    { code: 'el', name: 'Greco', native: 'Ελληνικά' },
    { code: 'he', name: 'Ebraico', native: 'עברית' },
    { code: 'th', name: 'Thai', native: 'ไทย' },
    { code: 'vi', name: 'Vietnamita', native: 'Tiếng Việt' },
  ];
  
  const [selectedTemplate, setSelectedTemplate] = useState('elegant');
  const [selectedLanguage, setSelectedLanguage] = useState(currentLanguage);
  const [headerMode, setHeaderMode] = useState<'logo' | 'text'>('logo');
  const [pdfBasicColor, setPdfBasicColor] = useState('#333333');
  const [pdfAccentColor, setPdfAccentColor] = useState('#c4a77d');
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationProgress, setGenerationProgress] = useState(0);
  const [generationStatus, setGenerationStatus] = useState('');
  const [isLanguageDropdownOpen, setIsLanguageDropdownOpen] = useState(false);
  
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsLanguageDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Get current selected language info
  const getSelectedLanguageInfo = () => {
    return languages.find(lang => lang.code === selectedLanguage) || languages[0];
  };

  // Generate PDF: backend always fetches fresh menu from DB and translates if needed
  const generatePDF = async () => {
    setIsGenerating(true);
    setGenerationProgress(0);
    setGenerationStatus('Preparazione...');

    try {
      const token = localStorage.getItem('token');
      
      setGenerationProgress(20);
      setGenerationStatus(
        selectedLanguage !== 'it'
          ? 'Traduzione e generazione PDF in corso...'
          : 'Generazione del PDF in corso...'
      );
      
      // Backend fetches current menu from DB (so deletions/reorders are reflected) and translates if language !== 'it'
      const response = await axios.post(
        `/menu/${menuId}/generate-template-pdf`,
        { 
          template: selectedTemplate, 
          language: selectedLanguage,
          headerMode,
          pdfBasicColor,
          pdfAccentColor
        },
        {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          responseType: 'blob',
          validateStatus: () => true
        }
      );
      
      if (response.status !== 200) {
        let message = 'Errore durante la generazione del PDF';
        try {
          const text = await (response.data as Blob).text();
          const json = JSON.parse(text);
          message = json.message || json.error || message;
        } catch (_) {}
        throw new Error(message);
      }
      
      // Update progress
      setGenerationProgress(90);
      setGenerationStatus('Download del PDF in corso...');
      
      // Download PDF
      const blob = new Blob([response.data], { type: 'application/pdf' });
      if (blob.size === 0) throw new Error('PDF vuoto ricevuto');
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `menu_${menuId}_${selectedTemplate}_${selectedLanguage}.pdf`);
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 100);
      
      // Complete progress
      setGenerationProgress(100);
      setGenerationStatus('Completato!');
      
      // Show success message
      toast.success('PDF generato con successo!');
      
      // Reset after a delay
      setTimeout(() => {
        setIsGenerating(false);
        setGenerationProgress(0);
        setGenerationStatus('');
      }, 1000);
      
    } catch (error) {
      console.error('PDF generation failed:', error);
      toast.error('Errore durante la generazione del PDF');
      
      // Reset on error
      setIsGenerating(false);
      setGenerationProgress(0);
      setGenerationStatus('');
    }
  };

  return (
    <div className="space-y-6">
      {/* Template Selection */}
      <div className="bg-white rounded-lg border p-6">
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Palette className="w-5 h-5" />
          Seleziona Template
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {templates.map((template) => (
            <button
              key={template.id}
              onClick={() => setSelectedTemplate(template.id)}
              className={`p-4 rounded-lg border-2 transition-all ${
                selectedTemplate === template.id
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="text-center">
                <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-2">
                  <Palette className="w-5 h-5 text-gray-600" />
                </div>
                <h4 className="font-medium">{template.name}</h4>
                <p className="text-sm text-gray-600 mt-1">{template.description}</p>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Language Selection - Now as Dropdown */}
      <div className="bg-white rounded-lg border p-6">
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Languages className="w-5 h-5" />
          Seleziona Lingua
        </h3>
        
        <div className="relative" ref={dropdownRef}>
          {/* Dropdown Trigger Button */}
          <button
            type="button"
            onClick={() => setIsLanguageDropdownOpen(!isLanguageDropdownOpen)}
            className="w-full p-3 text-left bg-white border border-gray-300 rounded-lg hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all flex items-center justify-between"
          >
            <div className="flex items-center gap-3">
              <Globe className="w-5 h-5 text-gray-500" />
              <div>
                <div className="font-medium">{getSelectedLanguageInfo().native}</div>
                <div className="text-sm text-gray-600">{getSelectedLanguageInfo().name}</div>
              </div>
            </div>
            <ChevronDown className={`w-5 h-5 text-gray-500 transition-transform ${isLanguageDropdownOpen ? 'transform rotate-180' : ''}`} />
          </button>
          
          {/* Dropdown Menu */}
          {isLanguageDropdownOpen && (
            <div className="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded-lg shadow-lg max-h-80 overflow-y-auto">
              <div className="p-2">
                <div className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Lingue disponibili ({languages.length})
                </div>
                
                {languages.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => {
                      setSelectedLanguage(lang.code);
                      setIsLanguageDropdownOpen(false);
                    }}
                    className={`w-full text-left px-4 py-3 rounded-md flex items-center justify-between transition-colors ${
                      selectedLanguage === lang.code
                        ? 'bg-blue-50 text-blue-700'
                        : 'hover:bg-gray-50 text-gray-700'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-2 h-2 rounded-full ${selectedLanguage === lang.code ? 'bg-blue-500' : 'bg-gray-300'}`} />
                      <div>
                        <div className="font-medium">{lang.native}</div>
                        <div className="text-sm text-gray-600">{lang.name}</div>
                      </div>
                    </div>
                    {selectedLanguage === lang.code && (
                      <CheckCircle className="w-4 h-4 text-blue-500" />
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
        
        <p className="mt-3 text-sm text-gray-500">
          Seleziona la lingua per la traduzione del menu. Verrà generato un PDF nella lingua selezionata.
        </p>
      </div>

      {/* PDF options: header (logo vs text) and colors */}
      <div className="bg-white rounded-lg border p-6">
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Palette className="w-5 h-5" />
          Opzioni PDF
        </h3>
        <div className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Intestazione</label>
            <div className="flex rounded-lg border border-gray-300 overflow-hidden">
              <button
                type="button"
                onClick={() => setHeaderMode('logo')}
                className={`flex-1 flex items-center justify-center gap-2 py-3 px-4 text-sm font-medium transition-colors ${
                  headerMode === 'logo'
                    ? 'bg-blue-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                <Image className="w-4 h-4" />
                Logo
              </button>
              <button
                type="button"
                onClick={() => setHeaderMode('text')}
                className={`flex-1 flex items-center justify-center gap-2 py-3 px-4 text-sm font-medium transition-colors ${
                  headerMode === 'text'
                    ? 'bg-blue-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                <Type className="w-4 h-4" />
                Nome ristorante
              </button>
            </div>
            <p className="mt-1 text-xs text-gray-500">Scegli se mostrare il logo o solo il nome del ristorante in testa al PDF.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Colore testo principale</label>
              <p className="text-xs text-gray-500 mb-2">Titoli, piatti, descrizioni, footer</p>
              <div className="flex items-center gap-3">
                <input
                  type="color"
                  value={pdfBasicColor}
                  onChange={(e) => setPdfBasicColor(e.target.value)}
                  className="w-10 h-10 rounded border border-gray-300 cursor-pointer"
                />
                <input
                  type="text"
                  value={pdfBasicColor}
                  onChange={(e) => setPdfBasicColor(e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg font-mono text-sm"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Colore accento</label>
              <p className="text-xs text-gray-500 mb-2">Prezzi e elementi evidenziati (default: colore tema)</p>
              <div className="flex items-center gap-3">
                <input
                  type="color"
                  value={pdfAccentColor}
                  onChange={(e) => setPdfAccentColor(e.target.value)}
                  className="w-10 h-10 rounded border border-gray-300 cursor-pointer"
                />
                <input
                  type="text"
                  value={pdfAccentColor}
                  onChange={(e) => setPdfAccentColor(e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg font-mono text-sm"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Generate Button Section */}
      <div className="bg-white rounded-lg border p-6">
        <div className="flex flex-col lg:flex-row gap-6 items-center">
          <div className="flex-1">
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="text-sm text-gray-600">Template</div>
                <div className="font-semibold">
                  {templates.find(t => t.id === selectedTemplate)?.name}
                </div>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="text-sm text-gray-600">Lingua</div>
                <div className="font-semibold">
                  {getSelectedLanguageInfo().native}
                </div>
              </div>
            </div>
            
            {/* Progress Bar - Only show during generation */}
            {isGenerating && (
              <div className="mt-4">
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-blue-600">{generationStatus}</span>
                  <span className="font-medium">{generationProgress}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${generationProgress}%` }}
                  ></div>
                </div>
              </div>
            )}
          </div>
          
          <div className="flex flex-col gap-3">
            <button
              onClick={generatePDF}
              disabled={isGenerating}
              className={`px-8 py-4 rounded-lg flex items-center justify-center gap-3 transition-all ${
                isGenerating ? 'bg-blue-500 cursor-wait' : 'bg-blue-600 hover:bg-blue-700'
              } text-white disabled:opacity-50 min-w-[200px]`}
            >
              {isGenerating ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span>Generazione...</span>
                </>
              ) : (
                <>
                  <FileText className="w-5 h-5" />
                  <span>Genera PDF</span>
                </>
              )}
            </button>
          </div>
        </div>
        
        <div className="mt-4 text-sm text-gray-600">
          <p>• Ogni volta viene generato un nuovo PDF con template e lingua selezionati</p>
          <p>• Se scegli una lingua diversa dall&apos;italiano viene usata la traduzione (Google quando configurato)</p>
          <p>• Categorie e piatti rispettano l&apos;ordine impostato nella scheda Categorie e piatti</p>
        </div>
      </div>
    </div>
  );
};

export default TemplatePDFGenerator;